import environment from '../../common/environment';
import { ariaLabelWithNewTab } from '../../common/utils/a11y-util';
import { BaseComponent } from '../base-component/base-component';
import { FreeTrialInfo } from './interfaces/free-trial-info.interface';
import { booleanConverter } from '../../common/converters';
import { customElement, property } from 'lit/decorators.js';
import { CANCELATION_FREE_TRIAL_HELP_URL, getFreeTrialDuration } from './utils/free-trial.util';
import { html } from 'lit';
import emit from '../../common/events';

/**
 * A static element to display agreements and cancellation info for free trial products.
 */
@customElement('mhecomm-free-trial-agreements')
export class FreeTrialAgreements extends BaseComponent {
  /**
   * A free trial object.
   */
  @property({ type: Object })
  freeTrial?: FreeTrialInfo;

  /**
   * The free trial duration period.
   */
  @property({ type: String })
  freeTrialDurationPeriod = 'day';

  /**
   * Whether to render the base checkout agreements.
   */
  @property({ converter: booleanConverter })
  showBaseAgreements = true;

  /**
   * Whether to render the free trial agreements.
   */
  @property({ converter: booleanConverter })
  showAgreements = true;

  /**
   * Whether to render the cancellation info.
   */
  @property({ converter: booleanConverter })
  showCancellationInfo = true;

  renderComp() {
    return html`
      ${this.showAgreements ? this.getAgreements() : ''} ${this.showCancellationInfo ? this.getCancellationInfo() : ''}
    `;
  }

  /**
   * Returns the base checkout agreements.
   */
  getBaseAgreements() {
    return html` <mhecomm-checkout-agreements legalCopyPrefix='By clicking "Place Order" you agree to our' /> `;
  }

  /**
   * Returns the free trial agreements.
   */
  getAgreements() {
    return html`
      <p class="fst-italic mb-0">
        ${this.showBaseAgreements ? this.getBaseAgreements() : ''} After your ${this.getDuration()} free trial, your
        payment card will automatically be charged ${this.getPrice()}. You may ${this.getCancellationTerms('cancel')} at any time during your
        ${this.getDuration()} free trial to avoid being charged the subscription fee. There are no refunds or credits
        for partial subscription periods.
      </p>
    `;
  }

  /**
   * Returns the cancellation info for the free trial.
   */
  getCancellationInfo() {
    const orderHistoryUrl = `${environment().mheducationUrl}/account#orderHistory`;
    return html`
      <p class="mt-2 mb-0">
        Cancel before your free trial ends in
        <a
          href=${orderHistoryUrl}
          class="fw-bold"
          @click=${() => this.fireAnalytics('My Account Order History')}
          aria-label="${ariaLabelWithNewTab('View order history')}"
          target="_blank"
          >My Account &gt; Order History</a
        >
        to avoid getting billed.
        ${this.getCancellationTerms('How do I cancel a free trial?')}
      </p>
    `;
  }

  /**
   * Renders the cancellation terms for free trial products.
   */
  getCancellationTerms(text: string) {
    return html `
      <a
        href=${CANCELATION_FREE_TRIAL_HELP_URL}
        aria-label="${ariaLabelWithNewTab('How do I cancel a free trial?')}"
        target="_blank"
        class="${this.showCancellationInfo ? 'fw-bold' : ''}"
        @click=${() => this.fireAnalytics('How do I cancel a free trial')}
        >${text}</a
      >
    `
  }

  /**
   * Returns the free trial duration and period in lowercase, separated by a dash.
   */
  getDuration(): string {
    const duration = getFreeTrialDuration(this.freeTrial?.startDate, this.freeTrial?.endDate);

    if (!duration || duration === '0' || !this.freeTrialDurationPeriod) {
      return '';
    }

    return `${duration}-${this.freeTrialDurationPeriod}`.toLowerCase();
  }

  /**
   * Returns the free trial subscription unit price.
   */
  getPrice() {
    if (!this.freeTrial?.subscriptionUnitPrice) {
      return html``;
    }

    return html`$${this.freeTrial.subscriptionUnitPrice}`;
  }

  private fireAnalytics(eventName) {
    emit({
      element: this,
      name: 'click',
      detail: {
        label: eventName,
        eventName: eventName.toLowerCase().replace(/\s/g, '-'),
        url: window.location.href,
      },
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-free-trial-agreements': FreeTrialAgreements;
  }
}
